import { educationOverviewQueryOptions } from '@/api/educationQueryOptions';
import { fetchDetailPermissionsQueryOptions } from '@/api/permissionsQueryOptions';
import { ApiErrorComponent } from '@/components/ApiErrorComponent';
import { useColumnState } from '@/components/hooks/useColumnState';
import useGridPageSize from '@/components/hooks/useGridPageSize';
import { usePersistedVisibilityModel } from '@/components/hooks/usePersistedVisibilityModel';
import useResponsive from '@/components/hooks/useResponsive';
import LoadProgress from '@/components/LoadProgress';
import { OverviewGridToolbar } from '@/components/mui-x/datagrid/OverviewGridToolbar';
import { getOrganization } from '@/store/auth/authSlice';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Box, Button } from '@mui/material';
import { GridColDef, GridColumnVisibilityModel, GridLogicOperator } from '@mui/x-data-grid';
import { DataGridPremium, useGridApiRef } from '@mui/x-data-grid-premium';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { t } from 'i18next';
import React from 'react';
import { useSelector } from 'react-redux';

export const Route = createFileRoute('/_auth/education')({
    component: EducationComponent,
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
})

function EducationComponent() {

    // const sessionOrganization = useSelector(getOrganization);

    const educationQuery = useSuspenseQuery(educationOverviewQueryOptions())
    const educations = educationQuery.data;

    const sessionOrganization = useSelector(getOrganization)

    const permissionsQuery = useSuspenseQuery(fetchDetailPermissionsQueryOptions('organization', sessionOrganization.uuid, ['EducationUpload']))
    const permissions = permissionsQuery.data
    const uploadEducations = permissions && permissions.filter(p => p.detail === 'EducationUpload' && p.permissions.some(a => a === 'write')).length > 0


    // const navigate = useNavigate();

    const organizations = [...new Set(educations.map((edu) => edu.currentOrganizationName))].sort()
    const tracks = [...new Set(educations.map((edu) => edu.trackCode + ' - ' + edu.trackName))].sort()
    const steps = [...new Set(educations.map((edu) => edu.stepCode + ' - ' + edu.stepName))].sort()

    const { xsmall, small, medium, large, xlarge } = useResponsive();

    const columns: GridColDef[] = [];
    if (xsmall || small || medium) {
        columns.push({
            field: 'member',
            headerName: 'Mitglied',
            valueGetter: (_value, row) => `${row.scoutId} - ${row.firstname} ${row.lastname}`,
            renderCell: (params) => {
                return (
                    <Link
                        to="/member/$memberUuid/main"
                        params={{
                            memberUuid: params.row.memberUuid,
                        }}
                        preload="intent"
                    >
                        {params.row.scoutId} - {params.row.firstname} {params.row.lastname}
                    </Link>
                )
            },

        },
        )
    } else {
        columns.push({
            field: 'scoutId',
            headerName: 'Scout-ID',
            valueGetter: (_value, row) => `${row.scoutId}`,
            renderCell: (params) => {
                return (
                    <Link
                        to="/member/$memberUuid/main"
                        params={{
                            memberUuid: params.row.memberUuid,
                        }}
                        preload="intent"
                    >
                        {params.row.scoutId}
                    </Link>
                )
            },

        },
            {
                field: 'firstname',
                headerName: 'Vorname',
                valueGetter: (_value, row) => `${row.firstname}`,
                renderCell: (params) => {
                    return (
                        <Link
                            to="/member/$memberUuid/main"
                            params={{
                                memberUuid: params.row.memberUuid,
                            }}
                            preload="intent"
                        >
                            {params.row.firstname}
                        </Link>
                    )
                },

            },
            {
                field: 'lastname',
                headerName: 'Nachname',
                valueGetter: (_value, row) => `${row.lastname}`,
                renderCell: (params) => {
                    return (
                        <Link
                            to="/member/$memberUuid/main"
                            params={{
                                memberUuid: params.row.memberUuid,
                            }}
                            preload="intent"
                        >
                            {params.row.lastname}
                        </Link>
                    )
                },

            }
        )
    }
    columns.push(
        {
            field: 'currentOrganization',
            headerName: t('property.membership.currentOrganization'),
            type: 'singleSelect',
            valueOptions: organizations,
            valueGetter: (_value, row) => `${row.currentOrganizationName}`,

        },
        {
            field: 'track',
            headerName: t('entity.educationTrack'),
            type: 'singleSelect',
            valueOptions: tracks,
            valueGetter: (_value, row) => `${row.trackCode} - ${row.trackName}`,

        },
        {
            field: 'step',
            headerName: t('entity.educationStep'),
            type: 'singleSelect',
            valueOptions: steps,
            valueGetter: (_value, row) => `${row.stepCode} - ${row.stepName}`,

        },
        {
            field: 'completeDate',
            headerName: t('property.memberEducationStep.completeDate'),
            type: 'date',
            valueGetter: (value,) => value && new Date(value),

        },
        {
            field: 'approver',
            headerName: t('property.memberEducationStep.approver'),
        },
        {
            field: 'approveMember',
            headerName: t('property.memberEducationStep.approveMember'),
        },
        {
            field: 'approveOrg',
            headerName: t('property.memberEducationStep.approveOrganization'),
        },
        {
            field: 'comment',
            headerName: t('property.general.comment'),
        },
    )

    const initialVisibilityModel = {
        'member': xsmall || small || medium,
        'firstname': large || xlarge,
        'lastname': large || xlarge,
        'scoutId': large || xlarge,
        'currentOrganization': large || xlarge,
        'track': large || xlarge,
        'step': true,
        'endDate': large || xlarge,
        'description': false,
    } as GridColumnVisibilityModel

    const [visibilityModel, setVisibilityModel] = usePersistedVisibilityModel('education', initialVisibilityModel)
    const handleVisibilityModelChange = React.useCallback((model: GridColumnVisibilityModel) => {
        setVisibilityModel(model)
    }, [setVisibilityModel]);

    const gridPageSize = useGridPageSize()
    const pageSizeOptions = gridPageSize.getPageSizeOptions('education', 35)
    const [paginationModel, setPaginationModel] = React.useState(gridPageSize.getPaginationModel('education', 35))

    const apiRef = useGridApiRef()
    const columnsState = useColumnState('educationOverview', apiRef, columns)

    if (educationQuery.isLoading || educationQuery.isFetching || !educations) {
        return <LoadProgress />
    }

    return (
        <Box sx={{
            height: { xs: `calc(100% - 50px)` }
        }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>

                <DataGridPremium
                    columns={columnsState.columns}
                    onColumnWidthChange={columnsState.onColumnWidthChange}
                    onColumnOrderChange={columnsState.onColumnOrderChange}
                    rows={educations}
                    getRowId={(row) => { return row.uuid || '' }}
                    pagination
                    paginationModel={paginationModel}
                    onPaginationModelChange={(model) => {
                        setPaginationModel(model)
                        gridPageSize.handlePaginationModelChange('education', model)
                    }}
                    pageSizeOptions={pageSizeOptions}
                    columnVisibilityModel={visibilityModel}
                    onColumnVisibilityModelChange={handleVisibilityModelChange}
                    slots={{
                        toolbar: (props) => {
                            return <OverviewGridToolbar {...props} scope="education">
                                {uploadEducations &&
                                    <Link
                                        to={'/educationImport'}>
                                        <Button variant="text" startIcon={<FileUploadIcon />}>Import</Button>
                                    </Link>
                                }
                            </OverviewGridToolbar>
                        }
                    }}
                    slotProps={{
                        filterPanel: {
                            logicOperators: [GridLogicOperator.And],
                            sx: { maxWidth: '100vw' }
                        },
                        toolbar: {
                            csvOptions: {
                                disableToolbarButton: true
                            },
                            printOptions: {
                                disableToolbarButton: true
                            },
                        }
                    }}
                    density='compact'
                    hideFooter={true}
                    disableDensitySelector
                    disableAggregation
                    disableColumnPinning
                    disableColumnReorder
                    disableRowGrouping
                    disablePivoting
                    showToolbar />
            </div>
        </Box>
    );
}
